import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Lock,
  Eye,
  EyeSlash,
  CheckCircle,
  Warning,
  Sparkle,
  ShieldCheck
} from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetupPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setTokenData, setUserData } = useAuth();
  
  const [tokenValid, setTokenValid] = useState(null); // null = loading, true/false = result
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError('No setup token provided. Please use the link from your email.');
      return;
    }

    // Validate the token
    const validateToken = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/setup-password/validate?token=${token}`);
        setTokenValid(true);
        setUserInfo(response.data);
      } catch (err) {
        setTokenValid(false);
        setError(err.response?.data?.detail || 'Invalid or expired setup link. Please contact support.');
      }
    };

    validateToken();
  }, [token]);

  const validatePassword = () => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validatePassword();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/setup-password`, {
        token,
        password
      });
      
      // Store token and user data
      const { token: authToken, user } = response.data;
      
      // Store token in localStorage FIRST (synchronously)
      localStorage.setItem('menopath_token', authToken);
      
      // Mark that this is a NEW user who needs onboarding (will be shown once)
      localStorage.setItem(`menopath_onboarding_${user.user_id}`, 'needs_onboarding');
      
      // Then update React state
      setTokenData(authToken);
      setUserData(user);
      
      setSuccess(true);
      toast.success('Account created successfully!');
      
      // Redirect to dashboard after a short delay to ensure state is updated
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-[#F8F6FF] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#5B21B6] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#6B7280]">Validating your setup link...</p>
        </div>
      </div>
    );
  }

  // Invalid token
  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-[#F8F6FF] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          <div className="glass-card rounded-3xl p-10 text-center">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <Warning className="w-10 h-10 text-red-500" weight="fill" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E1B4B] font-['Outfit'] mb-4">
              Invalid Setup Link
            </h1>
            <p className="text-[#6B7280] mb-6">{error}</p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-[#5B21B6] hover:bg-[#4C1D95] rounded-full">
                  Go to Login
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="outline" className="w-full rounded-full">
                  Request New Access
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#F8F6FF] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          <div className="glass-card rounded-3xl p-10 text-center">
            <div className="w-20 h-20 rounded-full bg-[#D1FAE5] flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#059669]" weight="fill" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E1B4B] font-['Outfit'] mb-4">
              Account Created!
            </h1>
            <p className="text-[#6B7280] mb-4">
              Welcome to MenoPath, <strong>{userInfo?.name}</strong>!
            </p>
            <p className="text-sm text-[#6B7280]">
              Redirecting you to your dashboard...
            </p>
            <div className="mt-6">
              <div className="w-8 h-8 border-4 border-[#5B21B6] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-[#F8F6FF] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="glass-card rounded-3xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] flex items-center justify-center mx-auto mb-4">
              <Sparkle className="w-8 h-8 text-white" weight="fill" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E1B4B] font-['Outfit'] mb-2">
              Complete Your Account
            </h1>
            <p className="text-[#6B7280]">
              Welcome, <strong className="text-[#5B21B6]">{userInfo?.name}</strong>!
              <br />Create a password to access MenoPath.
            </p>
          </div>

          {/* Email display */}
          <div className="bg-[#EDE9FE] rounded-xl p-4 mb-6">
            <p className="text-sm text-[#5B21B6] text-center">
              Setting up account for: <strong>{userInfo?.email}</strong>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1E1B4B] font-medium">
                Create Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" weight="duotone" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="h-12 pl-12 pr-12 rounded-xl border-2 focus:border-[#5B21B6]"
                  required
                  minLength={8}
                  data-testid="setup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#5B21B6]"
                >
                  {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#1E1B4B] font-medium">
                Confirm Password
              </Label>
              <div className="relative">
                <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" weight="duotone" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className={`h-12 pl-12 rounded-xl border-2 focus:border-[#5B21B6] ${
                    confirmPassword && password !== confirmPassword ? 'border-red-400' : ''
                  }`}
                  required
                  data-testid="setup-confirm-password-input"
                />
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <Warning className="w-4 h-4" weight="fill" />
                  Passwords do not match
                </p>
              )}
            </div>

            {/* Password requirements */}
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm font-medium text-[#1E1B4B] mb-2">Password requirements:</p>
              <ul className="text-sm text-[#6B7280] space-y-1">
                <li className={`flex items-center gap-2 ${password.length >= 8 ? 'text-green-600' : ''}`}>
                  {password.length >= 8 ? (
                    <CheckCircle className="w-4 h-4" weight="fill" />
                  ) : (
                    <span className="w-4 h-4 rounded-full border border-gray-300" />
                  )}
                  At least 8 characters
                </li>
              </ul>
            </div>

            <Button
              type="submit"
              disabled={loading || password.length < 8 || password !== confirmPassword}
              className="w-full h-12 bg-[#5B21B6] hover:bg-[#4C1D95] rounded-xl text-lg font-semibold"
              data-testid="setup-submit-btn"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-sm text-[#6B7280] mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#5B21B6] font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
